'use client';

import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { useTranslations, useLocale } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { ArrowLeft, Menu, X } from 'lucide-react';
import BlogCard from '@/components/landing/BlogCard';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import NewsletterSignup from '@/components/newsletter/NewsletterSignup';
import { useState } from 'react';

interface CategoryPageClientProps {
  slug: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialResult?: Record<string, any> | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialCategories?: Record<string, any>[];
}

export default function CategoryPageClient({ slug, initialResult, initialCategories }: CategoryPageClientProps) {
  const t = useTranslations('blog');
  const tCat = useTranslations('blogCategory');
  const tNav = useTranslations('nav');
  const tFooter = useTranslations('footer');
  const locale = useLocale();
  const isZh = locale === 'zh-TW';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const resultLive = useQuery(api.blogPosts.getPostsByCategory, { categorySlug: slug, limit: 50 });
  const result = resultLive ?? initialResult;
  const categoriesLive = useQuery(api.blogCategories.getActiveCategories);
  const categories = categoriesLive ?? initialCategories;

  const categoryName = result?.category
    ? (isZh && result.category.nameZh ? result.category.nameZh : result.category.name)
    : slug.replace(/-/g, ' ');

  const categoryDescription = result?.category
    ? (isZh && result.category.descriptionZh ? result.category.descriptionZh : result.category.description)
    : '';

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link href="/" className="flex items-center gap-2">
              <span className="text-xl font-bold text-foreground">StyTrix</span>
            </Link>
            <nav aria-label="Main navigation" className="hidden md:flex items-center gap-6">
              <Link href="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200">{tNav('features')}</Link>
              <Link href="/blog" className="text-sm text-primary font-medium">{tNav('blog')}</Link>
              <LanguageSwitcher />
            </nav>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-muted-foreground"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-lg px-4 py-4 space-y-3">
            <Link href="/" className="block text-sm text-muted-foreground">{tNav('features')}</Link>
            <Link href="/blog" className="block text-sm text-primary font-medium">{tNav('blog')}</Link>
            <LanguageSwitcher />
          </div>
        )}
      </header>

      <main id="main-content" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back to blog */}
        <Link
          href="/blog"
          className="group inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4 transition-transform duration-200 group-hover:-translate-x-1" />
          {t('backToBlog')}
        </Link>

        {/* Category Header */}
        <div className="mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            {categoryName}
          </h1>
          {categoryDescription && (
            <p className="text-lg text-muted-foreground max-w-2xl">
              {categoryDescription}
            </p>
          )}
        </div>

        {/* Category Filter */}
        {categories && categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10">
            <Link
              href="/blog"
              className="px-4 py-2 text-sm rounded-full border border-border text-muted-foreground hover:bg-secondary hover:-translate-y-[1px] hover:shadow-sm transition-all duration-200"
            >
              {t('allCategories')}
            </Link>
            {categories.map((cat: any) => {
              const isActive = cat.slug === slug;
              const name = isZh && cat.nameZh ? cat.nameZh : cat.name;
              return (
                <Link
                  key={cat._id}
                  href={`/blog/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm rounded-full border transition-all duration-200 ${
                    isActive
                      ? 'bg-primary text-white border-primary shadow-lg shadow-primary/25 scale-[1.02]'
                      : 'border-border text-muted-foreground hover:bg-secondary hover:-translate-y-[1px] hover:shadow-sm'
                  }`}
                >
                  {name}
                </Link>
              );
            })}
          </div>
        )}

        {/* Posts Grid */}
        {!result ? (
          <div className="text-center py-20 text-muted-foreground">{t('loading')}</div>
        ) : result.posts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-xl text-muted-foreground">{t('noArticles')}</p>
            <p className="text-sm text-muted-foreground mt-2">{t('noArticlesEmpty')}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {result.posts.map((post: any) => (
              <BlogCard
                key={post._id}
                title={post.title}
                titleZh={post.titleZh}
                slug={post.slug}
                excerpt={post.excerpt}
                excerptZh={post.excerptZh}
                featuredImageUrl={post.r2FeaturedImageUrl || post.featuredImageUrl}
                featuredImageAlt={post.featuredImageAlt}
                categoryName={result.category?.name || slug}
                categorySlug={post.categorySlug || slug}
                categoryColor={result.category?.color}
                readingTimeMinutes={post.readingTimeMinutes}
                publishedAt={post.publishedAt}
              />
            ))}
          </div>
        )}

        {/* Newsletter */}
        <div className="mt-16">
          <NewsletterSignup variant="card" source="category-page" />
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-background mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-sm text-muted-foreground">
            {tFooter('copyright', { year: new Date().getFullYear() })}
          </div>
        </div>
      </footer>
    </div>
  );
}
